import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrustBadge from './TrustBadge';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navigation = [
    {
      name: 'Products',
      items: [
        { name: 'AI Tools Hub', href: '/tools', desc: '268+ AI tools across 27 categories' },
        { name: 'Live Auctions', href: '/auctions', desc: 'Real-time bidding platform' },
        { name: 'Creator Hub', href: '/creator-hub', desc: 'Build and monetize' }
      ]
    },
    {
      name: 'AI Studios',
      items: [
        { name: 'Creation Studio', href: '/creation-studio', desc: 'Image, video, music, code & speech' },
        { name: 'AI Creative Studio', href: '/ai-creative-studio', desc: 'Multi-modal creative workflows' },
        { name: 'AI Training Studio', href: '/ai-training-studio', desc: 'Fine-tune & train custom models' },
        { name: 'Next-Gen AI Studio', href: '/next-gen-ai', desc: 'Frontier model playground' },
        { name: 'Gemini Studio', href: '/gemini-studio', desc: 'Google Gemini powered tools' },
        { name: 'Google Cloud Studio', href: '/google-cloud-studio', desc: 'Cloud-native AI services' },
        { name: 'TerraForge Studio', href: '/terraforge-studio', desc: 'AI-powered landscaping' },
        { name: 'Hybrid Integrations', href: '/hybrid-integrations', desc: 'Manus · Zed · Product Hunt · OpenClaw · more' },
        { name: 'Secrets Vault', href: '/secrets-vault', desc: 'Audit + sync 167 API keys · Infisical' }
      ]
    },
    {
      name: 'Solutions',
      items: [
        { name: 'For Creators', href: '/solutions/creators', desc: 'Monetize your AI skills' },
        { name: 'For Vendors', href: '/solutions/vendors', desc: 'Sell AI tools at scale' },
        { name: 'For Enterprises', href: '/solutions/enterprise', desc: 'Custom AI solutions' }
      ]
    },
    { name: 'Features', href: '/features' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Docs', href: '/docs' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-cyan-400" />
            <span className="text-xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              NEXUS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              item.items ? (
                <div key={item.name} className="relative group">
                  <button className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors">
                    {item.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-2 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4 shadow-2xl">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          data-testid={`nav-link-${subItem.href.replace(/\//g, '').toLowerCase()}`}
                          className="block p-3 rounded-xl hover:bg-zinc-800 transition-colors"
                        >
                          <div className="font-semibold text-white mb-1">{subItem.name}</div>
                          <div className="text-sm text-gray-400">{subItem.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <TrustBadge />
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-gray-300 hover:text-white"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-800">
            {navigation.map((item) => (
              <div key={item.name} className="py-2">
                {item.items ? (
                  <>
                    <div className="text-gray-400 text-sm font-semibold mb-2">{item.name}</div>
                    {item.items.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className="block py-2 text-gray-300 hover:text-white"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className="block py-2 text-gray-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="w-full"
              >
                Sign In
              </Button>
              <Button
                onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}